/**
 * lib/breadcrumbs.ts
 * ─────────────────────────────────────────────────────────────────────────────
 * In-memory ring buffer of recent client-side events. Attached to every error
 * report so the admin error log can show "what was the user doing right before
 * this crash" — Sentry-style breadcrumbs without the Sentry bill.
 *
 * Capacity is intentionally small (30) so the report payload stays tiny and
 * we don't keep stale references around. FIFO eviction.
 *
 * Module-level state — there is exactly one buffer per browser tab, shared
 * across every component that pushes to it. Server-side calls are no-ops.
 *
 * Usage:
 *   import { addBreadcrumb, getBreadcrumbs } from '@/lib/breadcrumbs'
 *
 *   addBreadcrumb({ category: 'modal_open', message: 'bug_report' })
 *   addBreadcrumb({ category: 'nav', message: '/products/dev → /home' })
 *
 *   // At error time:
 *   const trail = getBreadcrumbs()  // most recent last
 */

export type BreadcrumbCategory =
  | 'nav'           // route change
  | 'modal_open'    // global modal opened (BugReport, FeatureRequest, etc.)
  | 'modal_close'   // global modal closed
  | 'panel_open'    // slideout panel opened (ProductDetail, ContactDetail, etc.)
  | 'panel_close'
  | 'click'         // user click captured via data-track attribute
  | 'fetch_error'   // failed network request
  | 'auth'          // sign-in / sign-out / token refresh
  | 'custom'        // anything else

export interface Breadcrumb {
  /** Unix ms timestamp. Set automatically by addBreadcrumb. */
  ts: number
  category: BreadcrumbCategory
  /** Short human-readable message. Keep < 80 chars. */
  message: string
  /** Optional structured payload. Keep small — this ships with every error. */
  data?: Record<string, unknown>
}

const MAX = 30
const buffer: Breadcrumb[] = []

/**
 * Append a breadcrumb to the buffer. If the buffer is at capacity, the oldest
 * entry is evicted. No-op during SSR.
 */
export function addBreadcrumb(crumb: Omit<Breadcrumb, 'ts'>): void {
  if (typeof window === 'undefined') return

  // Coalesce duplicate consecutive entries (e.g. rapid duplicate nav events
  // from React strict mode or repeated `usePathname` re-runs). Cuts noise
  // significantly without losing signal.
  const last = buffer[buffer.length - 1]
  if (
    last &&
    last.category === crumb.category &&
    last.message === crumb.message &&
    Date.now() - last.ts < 200
  ) {
    return
  }

  buffer.push({ ts: Date.now(), ...crumb })
  while (buffer.length > MAX) buffer.shift()
}

/**
 * Return a copy of the current breadcrumb trail (oldest first, most recent
 * last). Safe to attach to error report payloads — the array is a fresh copy,
 * so further pushes won't mutate it.
 */
export function getBreadcrumbs(): Breadcrumb[] {
  return buffer.slice()
}

/**
 * Reset the buffer. Mostly useful for tests; production code should never
 * need to clear it (the ring evicts naturally).
 */
export function clearBreadcrumbs(): void {
  buffer.length = 0
}
